import React, { useState, useEffect, useCallback, useRef } from "react";
import { mealplanApi, recipesApi } from "../services/api";
import IngredientInput from "../components/IngredientInput";
import "./MealPlanPage.css";

const DAYS = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];
const DAY_LABELS = {monday:"Monday",tuesday:"Tuesday",wednesday:"Wednesday",thursday:"Thursday",friday:"Friday",saturday:"Saturday",sunday:"Sunday"};
const MEALS = ["breakfast","mid_morning","lunch","snacks","dinner"];
const MEAL_LABELS = {breakfast:"🌅 Breakfast",mid_morning:"🥐 Mid Morning",lunch:"☀️ Lunch",snacks:"🍎 Snacks",dinner:"🌙 Dinner"};
const STATUS_COLOR = {enough:"#22c55e",running_low:"#f59e0b",finished:"#ef4444"};
const STATUS_LABEL = {enough:"Enough",running_low:"Running Low",finished:"Finished"};

function getMonday(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split("T")[0];
}

function formatWeekLabel(dateStr) {
  const d = new Date(dateStr);
  const end = new Date(d);
  end.setDate(end.getDate() + 6);
  return `${d.toLocaleDateString("en-US",{month:"short",day:"numeric"})} – ${end.toLocaleDateString("en-US",{month:"short",day:"numeric"})}`;
}

function isPastWeek(weekDate) {
  const today = new Date();
  today.setHours(0,0,0,0);
  const monday = new Date(getMonday(today));
  const weekMonday = new Date(weekDate);
  return weekMonday < monday;
}

export default function MealPlanPage() {
  const [weekDate, setWeekDate] = useState(getMonday());
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTemplate, setIsTemplate] = useState(false);
  const [active, setActive] = useState(null);
  const [expandedMealId, setExpandedMealId] = useState(null);
  const [dishSearch, setDishSearch] = useState("");
  const [dishSuggestions, setDishSuggestions] = useState([]);
  const [showDishSearch, setShowDishSearch] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [cartMsg, setCartMsg] = useState(null);
  const [error, setError] = useState(null);
  const [sendingTomorrow, setSendingTomorrow] = useState(false);
  const [nutrition, setNutrition] = useState(null);
  const [loadingNutrition, setLoadingNutrition] = useState(false);
  const [showNutritionModal, setShowNutritionModal] = useState(false);
  const [commentary, setCommentary] = useState(null);
  const [loadingCommentary, setLoadingCommentary] = useState(false);
  const debounceRef = useRef(null);

  const locked = !isTemplate && isPastWeek(weekDate);

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await mealplanApi.getWeek(isTemplate ? "template" : weekDate);
      setPlan(data);
    } catch {
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [weekDate, isTemplate]);

  const fetchNutrition = useCallback(async () => {
    if (isTemplate || isPastWeek(weekDate)) return;
    try {
      const result = await mealplanApi.getWeekNutrition(weekDate);
      setNutrition(result);
    } catch {}
  }, [weekDate, isTemplate]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);
  useEffect(() => { fetchNutrition(); }, [fetchNutrition]);

  const getMeals = (day, mealType) =>
    plan?.meals?.filter((m) => m.day === day && m.meal_type === mealType) || [];

  const handleCellClick = (day, mealType) => {
    if (locked) return;
    const key = `${day}-${mealType}`;
    const activeKey = active ? `${active.day}-${active.mealType}` : null;
    if (activeKey === key) {
      setActive(null);
      setShowDishSearch(false);
      setDishSearch("");
      setExpandedMealId(null);
    } else {
      setActive({ day, mealType });
      setShowDishSearch(false);
      setDishSearch("");
      setExpandedMealId(null);
    }
  };

  const handleDishSearchChange = async (val) => {
    setDishSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length >= 1) {
      debounceRef.current = setTimeout(async () => {
        try {
          const results = await recipesApi.search(val.trim());
          setDishSuggestions(results);
          setShowDishSearch(true);
        } catch { setDishSuggestions([]); }
      }, 300);
    } else {
      setDishSuggestions([]);
      setShowDishSearch(false);
    }
  };

  const handleSelectDish = async (day, mealType, recipe) => {
    try {
      await mealplanApi.saveMeal(
        isTemplate ? "template" : weekDate,
        day, mealType, recipe.dish_name, isTemplate
      );
      const updatedPlan = await mealplanApi.getWeek(isTemplate ? "template" : weekDate);
      const meals = updatedPlan?.meals?.filter(
        (m) => m.day === day && m.meal_type === mealType && m.dish_name === recipe.dish_name
      );
      if (meals && meals.length > 0) {
        const meal = meals[meals.length - 1];
        if (recipe.ingredients.length > 0 && meal.ingredients.length === 0) {
          for (const ing of recipe.ingredients) {
            await mealplanApi.addIngredient(meal.id, ing.ingredient_name, null);
          }
        }
      }
      await fetchPlan();
      setDishSearch("");
      setDishSuggestions([]);
      setShowDishSearch(false);
    } catch { setError("Failed to add dish."); }
  };

  const handleDeleteMeal = async (mealId) => {
    try {
      await mealplanApi.deleteMeal(mealId);
      if (expandedMealId === mealId) setExpandedMealId(null);
      await fetchPlan();
    } catch { setError("Failed to delete meal."); }
  };

  const handleAddIngredient = async (mealId, name, inventoryItemId = null) => {
    if (!name.trim()) return;
    try {
      await mealplanApi.addIngredient(mealId, name.trim(), inventoryItemId);
      await fetchPlan();
    } catch { setError("Failed to add ingredient."); }
  };

  const handleDeleteIngredient = async (ingId) => {
    try {
      await mealplanApi.deleteIngredient(ingId);
      await fetchPlan();
    } catch { setError("Failed to delete ingredient."); }
  };

  const handleGetSummary = async () => {
    setSummaryLoading(true);
    setSummary(null);
    try {
      const data = await mealplanApi.getSummary(weekDate);
      setSummary(data);
    } catch { setError("Failed to load summary."); }
    finally { setSummaryLoading(false); }
  };

  const handleAddToCart = async () => {
    try {
      const result = await mealplanApi.addMissingToCart(weekDate);
      setCartMsg(result.added > 0
        ? `✅ Added ${result.added} item(s) to cart!`
        : `ℹ️ ${result.skipped} item(s) already in cart.`);
      setTimeout(() => setCartMsg(null), 3000);
      await handleGetSummary();
    } catch { setError("Failed to add to cart."); }
  };

  const handleCopyTemplate = async () => {
    try {
      await mealplanApi.copyTemplate(weekDate);
      await fetchPlan();
      setIsTemplate(false);
    } catch { setError("No template found. Create one first."); }
  };

  const handleSendTomorrow = async () => {
    setSendingTomorrow(true);
    try {
      const result = await mealplanApi.sendTomorrow();
      setCartMsg(`✅ ${result.message}`);
      setTimeout(() => setCartMsg(null), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send tomorrow's plan.");
    } finally {
      setSendingTomorrow(false);
    }
  };

  const handleGetCommentary = async () => {
    setLoadingCommentary(true);
    try {
      const result = await mealplanApi.getAiCommentary(weekDate);
      setCommentary(result.commentary);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to get AI commentary.");
    } finally {
      setLoadingCommentary(false);
    }
  };

  const handleGetNutrition = async () => {
    setLoadingNutrition(true);
    setNutrition(null);
    setCommentary(null);
    try {
      const result = await mealplanApi.getWeekNutrition(weekDate);
      setNutrition(result);
      setError(null);
      setTimeout(() => {
        document.querySelector(".mp-insights-section")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (err) {
      console.error("Insights error:", err);
      setError(err.response?.data?.detail || err.message || "Failed to get insights.");
    } finally {
      setLoadingNutrition(false);
    }
  };

  const changeWeek = (dir) => {
    const d = new Date(weekDate);
    d.setDate(d.getDate() + dir * 7);
    setWeekDate(d.toISOString().split("T")[0]);
    setSummary(null);
    setActive(null);
    setDishSearch("");
    setExpandedMealId(null);
    setNutrition(null);
    setCommentary(null);
  };

  const totalNeedToBuy = summary
    ? summary.missing.length + summary.low.length + summary.not_in_inventory.length
    : 0;
  const allStocked = summary && totalNeedToBuy === 0 && summary.enough.length > 0;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">📅 Meal Plan</h1>
          <p className="page-sub">Plan your week, track your ingredients</p>
        </div>
        <div className="mp-header-actions">
          <button className="btn-send-tomorrow" onClick={handleSendTomorrow} disabled={sendingTomorrow}>
            {sendingTomorrow ? "Sending…" : "📨 Tomorrow's Plan"}
          </button>
          {!isTemplate && !locked && (
            <button className="btn-send-tomorrow"
              style={{background:"rgba(124,106,247,0.15)",color:"var(--accent)",border:"1px solid var(--accent)"}}
              onClick={async () => {
                setShowNutritionModal(true);
                if (!nutrition) await handleGetNutrition();
              }}>
              {loadingNutrition ? "⏳ Loading…" : "📊 Nutrition"}
            </button>
          )}

        </div>
      </div>

      {error && <div className="error-banner" onClick={() => setError(null)}>{error} ✕</div>}
      {cartMsg && <div className="success-banner">{cartMsg}</div>}

      <div className="mp-topbar">
        <div className="mp-mode-bar">
          <button className={`mp-mode-btn ${!isTemplate ? "active" : ""}`}
            onClick={() => { setIsTemplate(false); setSummary(null); setActive(null); }}>
            📅 This Week
          </button>
          <button className={`mp-mode-btn ${isTemplate ? "active" : ""}`}
            onClick={() => { setIsTemplate(true); setSummary(null); setActive(null); }}>
            🔄 Template
          </button>
        </div>
        {!isTemplate && (
          <div className="mp-week-nav">
            <button className="mp-nav-btn" onClick={() => changeWeek(-1)}>‹</button>
            <span className="mp-week-label">
              {formatWeekLabel(weekDate)}
              {locked && <span className="mp-locked-badge">🔒 Past</span>}
            </span>
            <button className="mp-nav-btn" onClick={() => changeWeek(1)}>›</button>
            {!locked && <button className="mp-use-tmpl" onClick={handleCopyTemplate}>Use Template</button>}
          </div>
        )}
      </div>

      {isTemplate && (
        <div className="mp-template-hint">
          This plan repeats every week. Switch to "This Week" to override.
        </div>
      )}

      {locked && (
        <div className="mp-locked-banner">
          🔒 This is a past week — view only. Navigate forward to edit.
        </div>
      )}

      {loading ? (
        <div className="loading">Loading meal plan…</div>
      ) : (
        <div className="mp-days">
          {DAYS.map((day) => {
            const allMeals = MEALS.flatMap((mt) => getMeals(day, mt));
            const allDots = allMeals.flatMap((m) =>
              m.ingredients?.map((i) => i.inventory_status) || []
            ).filter(Boolean).slice(0, 6);
            const filledCount = MEALS.filter((mt) => getMeals(day, mt).length > 0).length;

            return (
              <div key={day} className={`mp-day-row ${locked ? "locked" : ""}`}>
                <div className="mp-day-header">
                  <span className="mp-day-name">{DAY_LABELS[day]}</span>
                  <div className="mp-day-dots">
                    {allDots.map((s, i) => (
                      <span key={i} className="mp-dot" style={{ background: STATUS_COLOR[s] || "#888" }} />
                    ))}
                    {filledCount === 0 && <span className="mp-no-meals">No meals planned</span>}
                  </div>
                </div>

                <div className="mp-meals-grid">
                  {MEALS.map((mealType) => {
                    const meals = getMeals(day, mealType);
                    const isActive = active?.day === day && active?.mealType === mealType;
                    return (
                      <div key={mealType}
                        className={`mp-meal-cell ${isActive ? "active" : ""} ${meals.length > 0 ? "filled" : ""} ${locked ? "locked" : ""}`}
                        onClick={() => handleCellClick(day, mealType)}
                      >
                        <div className="mp-meal-type">{MEAL_LABELS[mealType]}</div>
                        {meals.length > 0 ? (
                          <div className="mp-multi-dishes">
                            {meals.map((meal) => (
                              <div key={meal.id} className="mp-dish-chip">
                                <span className="mp-dish-chip-name">{meal.dish_name}</span>
                                {!locked && (
                                  <button className="mp-dish-chip-del"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteMeal(meal.id); }}>
                                    ✕
                                  </button>
                                )}
                              </div>
                            ))}
                            {!locked && <div className="mp-add-more">+ Add more</div>}
                          </div>
                        ) : locked ? (
                          <div className="mp-add-hint mp-locked-hint">—</div>
                        ) : (
                          <div className="mp-add-hint">+ Add dish</div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {active?.day === day && !locked && (
                  <div className="mp-expanded">
                    {(() => {
                      const meals = getMeals(day, active.mealType);
                      return (
                        <>
                          <div className="mp-exp-header">
                            <span className="mp-exp-title">{MEAL_LABELS[active.mealType]}</span>
                            <span className="mp-exp-sub">{meals.length} dish{meals.length !== 1 ? "es" : ""}</span>
                          </div>

                          {meals.map((meal) => (
                            <div key={meal.id} className="mp-exp-dish">
                              <div className="mp-exp-dish-header"
                                onClick={() => setExpandedMealId(expandedMealId === meal.id ? null : meal.id)}>
                                <span className="mp-exp-dish-name">📖 {meal.dish_name}</span>
                                <div className="mp-exp-dish-actions">
                                  <span className="mp-exp-dish-toggle">
                                    {expandedMealId === meal.id ? "▲" : "▼"} {meal.ingredients.length} ing
                                  </span>
                                  <button className="mp-ing-del"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteMeal(meal.id); }}>
                                    🗑
                                  </button>
                                </div>
                              </div>

                              {expandedMealId === meal.id && (
                                <div className="mp-exp-dish-body">
                                  {meal.ingredients.length === 0 ? (
                                    <p className="mp-no-ings">No ingredients added.</p>
                                  ) : (
                                    <div className="mp-ing-list">
                                      {meal.ingredients.map((ing) => (
                                        <div key={ing.id} className="mp-ing-row">
                                          <span className="mp-ing-dot" style={{
                                            background: ing.inventory_status
                                              ? STATUS_COLOR[ing.inventory_status] : "#888"
                                          }} />
                                          <span className="mp-ing-name">{ing.ingredient_name}</span>
                                          {ing.inventory_status ? (
                                            <span className={`mp-ing-tag mp-tag-${ing.inventory_status}`}>
                                              {STATUS_LABEL[ing.inventory_status]}
                                            </span>
                                          ) : (
                                            <span className="mp-ing-tag mp-tag-none">Not in inventory</span>
                                          )}
                                          <button className="mp-ing-del"
                                            onClick={() => handleDeleteIngredient(ing.id)}>✕</button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  <IngredientInput
                                    onAdd={(name, invId) => handleAddIngredient(meal.id, name, invId)}
                                    placeholder="Add ingredient…"
                                  />
                                </div>
                              )}
                            </div>
                          ))}

                          <div className="mp-add-dish-section">
                            <div className="mp-add-dish-label">Add dish from Meals tab:</div>
                            <div className="mp-dish-search-wrap">
                              <input
                                placeholder="Search dishes… (e.g. Poha)"
                                value={dishSearch}
                                onChange={(e) => handleDishSearchChange(e.target.value)}
                                className="mp-dish-search-input"
                              />
                              {showDishSearch && dishSuggestions.length > 0 && (
                                <div className="mp-dish-dropdown">
                                  {dishSuggestions.map((recipe) => (
                                    <div key={recipe.id} className="mp-dish-option"
                                      onMouseDown={() => handleSelectDish(day, active.mealType, recipe)}>
                                      <span className="mp-dish-option-name">{recipe.dish_name}</span>
                                      <span className="mp-dish-option-cat">{recipe.meal_category}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {showDishSearch && dishSuggestions.length === 0 && dishSearch.length > 0 && (
                                <div className="mp-dish-dropdown">
                                  <div className="mp-dish-no-results">
                                    No dishes found. Add it in the 🍽 Meals tab first.
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isTemplate && !locked && showNutritionModal && (
        <div className="modal-overlay" onClick={() => setShowNutritionModal(false)}>
        <div className="mp-nutrition-modal" onClick={e => e.stopPropagation()}>
          {loadingNutrition && <div className="loading">Loading nutrition…</div>}
          {!nutrition && !loadingNutrition && <div className="loading">No nutrition data available. Add nutrition in the 🍽 Meals tab first.</div>}
          {nutrition && (<>
          <div className="mp-nutrition-header">
            <h2 className="mp-nutrition-title">📊 Weekly Nutrition</h2>
            <button className="mp-nutrition-close" onClick={() => setShowNutritionModal(false)}>✕</button>
          </div>

          {/* Missing nutrition warning */}
          {nutrition.missing_nutrition?.length > 0 && (
            <div className="mp-nutrition-warning">
              ⚠️ No nutrition data for: {nutrition.missing_nutrition.join(", ")}. Add nutrition in the 🍽 Meals tab.
            </div>
          )}

          {/* Weekly averages */}
          {nutrition.weekly_avg && (
            <div className="mp-nutrition-avg">
              <div className="mp-nutrition-avg-label">Daily average vs recommended</div>
              <div className="mp-nutrition-avg-grid">
                {[
                  { key: "calories", label: "🔥 Cal", unit: "kcal", rec: "1800-2200", low: 1800, high: 2200 },
                  { key: "protein", label: "💪 Protein", unit: "g", rec: "50-60g", low: 50, high: 60 },
                  { key: "carbs", label: "🌾 Carbs", unit: "g", rec: "200-250g", low: 200, high: 250 },
                  { key: "fat", label: "🧈 Fat", unit: "g", rec: "44-78g", low: 44, high: 78 },
                  { key: "fibre", label: "🥦 Fibre", unit: "g", rec: "25-38g", low: 25, high: 38 },
                ].map(({ key, label, unit, rec, low, high }) => {
                  const val = nutrition.weekly_avg[key];
                  const status = val < low ? "low" : val > high ? "high" : "good";
                  const color = status === "good" ? "#22c55e" : status === "high" ? "#f59e0b" : "#ef4444";
                  const icon = status === "good" ? "✅" : status === "high" ? "⬆️" : "⬇️";
                  return (
                    <div key={key} className="mp-avg-card">
                      <div className="mp-avg-label">{label}</div>
                      <div className="mp-avg-value">{val}{key !== "calories" ? unit : ""}</div>
                      <div className="mp-avg-status" style={{ color }}>
                        {icon} {rec}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Per day breakdown */}
          <div className="mp-nutrition-days">
            {nutrition.days.filter(d => d.calories).map((day) => (
              <div key={day.day} className="mp-nutrition-day">
                <div className="mp-nutrition-day-header">
                  <span className="mp-nutrition-day-name">{day.label}</span>
                  <span className="mp-nutrition-day-cal">
                    ~{day.calories} kcal {day.calories >= 1800 && day.calories <= 2200 ? "✅" : day.calories > 2200 ? "⬆️" : "⬇️"}
                  </span>
                </div>
                {day.has_partial && (
                  <div className="mp-nutrition-partial">⚠️ Some dishes missing nutrition data</div>
                )}
                <div className="mp-nutrition-day-grid">
                  {[
                    { key: "protein", label: "💪 Protein", contrib: day.protein_contrib, low: 50, high: 60 },
                    { key: "carbs", label: "🌾 Carbs", contrib: day.carbs_contrib, low: 200, high: 250 },
                    { key: "fat", label: "🧈 Fat", contrib: day.fat_contrib, low: 44, high: 78 },
                    { key: "fibre", label: "🥦 Fibre", contrib: day.fibre_contrib, low: 25, high: 38 },
                  ].map(({ key, label, contrib, low, high }) => {
                    const val = day[key];
                    const status = val < low ? "low" : val > high ? "high" : "good";
                    const color = status === "good" ? "#22c55e" : status === "high" ? "#f59e0b" : "#ef4444";
                    const icon = status === "good" ? "✅" : status === "high" ? "⬆️" : "⬇️";
                    return (
                      <div key={key} className="mp-nutrition-cell">
                        <div className="mp-nutrition-cell-label">{label}</div>
                        <div className="mp-nutrition-cell-value" style={{ color }}>
                          {val}g {icon}
                        </div>
                        {contrib?.length > 0 && (
                          <div className="mp-nutrition-cell-contrib">{contrib.join(", ")}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* AI Commentary */}
          <div className="mp-ai-commentary">
            <div className="mp-ai-commentary-header">
              <span className="mp-ai-commentary-title">✨ AI Observations</span>
              <button className="mp-ai-refresh-btn"
                onClick={handleGetCommentary}
                disabled={loadingCommentary}>
                {loadingCommentary ? "Loading…" : commentary ? "↻ Refresh" : "Get commentary"}
              </button>
            </div>
            {commentary && (
              <div className="mp-ai-commentary-text">{commentary}</div>
            )}
            {!commentary && !loadingCommentary && (
              <div className="mp-ai-commentary-hint">
                Click "Get commentary" for AI observations on your week's nutrition.
              </div>
            )}
          </div>

          <div className="mp-nutrition-disclaimer">
            ⚠️ Estimates based on stored dish data. Not for medical use.
          </div>
          </>)}
        </div>
        </div>
      )}

      {!isTemplate && !locked && (
        <div className="mp-summary-section">
          <div className="mp-summary-header">
            <h2 className="mp-summary-title">🛒 Ingredient Summary</h2>
            <button className="mp-check-btn" onClick={handleGetSummary} disabled={summaryLoading}>
              {summaryLoading ? "Checking…" : "Check Inventory"}
            </button>
          </div>
          {summary && (
            <div className="mp-summary-content">
              {summary.missing.length > 0 && (
                <div className="mp-summary-group">
                  <div className="mp-summary-group-title" style={{color:"#ef4444"}}>
                    🔴 Finished ({summary.missing.length})
                  </div>
                  {summary.missing.map((i) => <div key={i.name} className="mp-summary-item">{i.name}</div>)}
                </div>
              )}
              {summary.low.length > 0 && (
                <div className="mp-summary-group">
                  <div className="mp-summary-group-title" style={{color:"#f59e0b"}}>
                    🟡 Running Low ({summary.low.length})
                  </div>
                  {summary.low.map((i) => <div key={i.name} className="mp-summary-item">{i.name}</div>)}
                </div>
              )}
              {summary.not_in_inventory.length > 0 && (
                <div className="mp-summary-group">
                  <div className="mp-summary-group-title" style={{color:"#7a7890"}}>
                    ⚪ Not in inventory ({summary.not_in_inventory.length})
                  </div>
                  {summary.not_in_inventory.map((i) => <div key={i.name} className="mp-summary-item">{i.name}</div>)}
                </div>
              )}
              {summary.enough.length > 0 && (
                <div className="mp-summary-group">
                  <div className="mp-summary-group-title" style={{color:"#22c55e"}}>
                    🟢 Enough ({summary.enough.length})
                  </div>
                  {summary.enough.map((n) => <div key={n} className="mp-summary-item">{n}</div>)}
                </div>
              )}
              {totalNeedToBuy > 0 && (
                <button className="mp-cart-btn" onClick={handleAddToCart}>
                  🛒 Add to Cart ({totalNeedToBuy} item{totalNeedToBuy !== 1 ? "s" : ""})
                </button>
              )}
              {allStocked && <div className="mp-all-good">🎉 All ingredients are stocked!</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
